import { useState } from "react";
import { CircleUserRound, LogOut, Sun, Moon, Plus, CreditCard, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import UpgradeDialog from "@/components/UpgradeDialog";
import { MobileActionSheet, ActionSheetItem, ActionSheetSeparator } from "@/components/MobileActionSheet";
import { useAccountActions } from "@/hooks/useAccountActions";

/**
 * Mobile replacement for the desktop icon rail's account actions.
 * Rendered only below md (the trigger is hidden on desktop).
 */
const MobileAccountMenu = () => {
  const [open, setOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const {
    subscription,
    isPaid,
    planBadge,
    trialDaysLeft,
    theme,
    toggleTheme,
    handleLogout,
    handleManageSubscription,
    connectAccount,
    isAtAccountLimit,
  } = useAccountActions();

  const planLabel = isPaid
    ? `${planBadge} plan`
    : subscription?.trialActive
      ? trialDaysLeft != null
        ? `Free trial — ${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left`
        : "Free trial"
      : "Free plan";

  const handleAddAccountClick = () => {
    setOpen(false);
    if (isAtAccountLimit) {
      setShowUpgradeDialog(true);
      return;
    }
    connectAccount();
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="hidden max-md:inline-flex rounded-full"
        onClick={() => setOpen(true)}
        aria-label="Account menu"
      >
        <CircleUserRound className="h-5 w-5 text-muted-foreground" />
      </Button>

      <MobileActionSheet open={open} onOpenChange={setOpen} title={planLabel}>
        <ActionSheetItem icon={Plus} label="Add account" onClick={handleAddAccountClick} />
        {isPaid ? (
          <ActionSheetItem
            icon={CreditCard}
            label="Manage subscription"
            onClick={() => {
              setOpen(false);
              handleManageSubscription();
            }}
          />
        ) : (
          <ActionSheetItem
            icon={Sparkles}
            label="See plans"
            onClick={() => {
              setOpen(false);
              setShowUpgradeDialog(true);
            }}
          />
        )}
        <ActionSheetItem
          icon={theme === "dark" ? Sun : Moon}
          label={theme === "dark" ? "Light mode" : "Dark mode"}
          onClick={toggleTheme}
        />
        <ActionSheetSeparator />
        <ActionSheetItem
          icon={LogOut}
          label="Log out"
          destructive
          onClick={() => {
            setOpen(false);
            setShowLogoutDialog(true);
          }}
        />
      </MobileActionSheet>

      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Confirm Logout</DialogTitle>
            <DialogDescription>
              Are you sure you want to log out?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowLogoutDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleLogout}>
              Logout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        source="add_account"
      />
    </>
  );
};

export default MobileAccountMenu;
